import { Injectable } from '@angular/core'
import {
  catchError,
  combineLatest,
  concatMap,
  distinctUntilChanged,
  firstValueFrom,
  from,
  lastValueFrom,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
  tap,
  toArray,
} from 'rxjs'

import { takeUntil, timeout } from 'rxjs/operators'

import { FirebaseError } from '@angular/fire/app'
import {
  setDoc,
  collection,
  deleteDoc,
  doc,
  docData,
  Firestore,
  writeBatch,
  DocumentReference,
  collectionSnapshots,
  query,
  updateDoc,
  orderBy,
  where,
  limit,
  documentId,
  getFirestore,
} from '@angular/fire/firestore'

import { DateOnly, log } from '@nx-superprep/utils'

import {
  ApplicationProfile,
  DayPlan, DayPlanModule,
  IncludedModuleInfo,
  ModuleStats, OverallStats,
  CardStats,
  SpacedRepModuleConfig,
  RangedModuleStats,
  RangedOverallStats,
  StudySession,
  UserResponse,
  UserStatsRange,
  AcquisitionCardRetrievalRequest,
  PracticeCardRetrievalRequest,
  ReviewCardRetrievalRequest,
  ApplicationSetting,
  StudySetting,
  StudyOrderProgress,
  StudyOrderTemplate,
  isRegularStudyPhase,
  QuizCardRetrievalRequest,
  Constants,
  StudyPhase,
  isQuizStudyPhase,
} from '../models/back-end'

import { FrontendCard, User } from '../models/front-end'

import { isEqual, uniqWith } from 'lodash-es'
import { LongtailService } from './longtail.service'
import { mapToEntities } from "./map-to-entities"

export interface DayPlanAndModules {
  dateOnly: DateOnly,
  plan: DayPlan,
  modules: DayPlanModule[],
  overall: OverallStats,
}

type DayPlanModuleSetting = {
  modules?: DayPlanModule[],
  plan?: DayPlan
  setting: ApplicationSetting
}

interface ActiveLesson {
  planAndModules: DayPlanAndModules,
  stats: RangedModuleStats,
  session: StudySession,
  module: DayPlanModule,
  isFirstTime: boolean,
}

@Injectable({providedIn: 'root'})
export class StudyService extends LongtailService {
  private _firestore: Firestore = getFirestore()

  private get firestore() {
    return this._firestore || getFirestore()
  }
  private set firestore(value) {
    this._firestore = value || getFirestore()
  }

  activeLesson: ActiveLesson | undefined

  setActiveLesson(phase: StudyPhase, plan: DayPlan, module: DayPlanModule, session: StudySession) {
    const shouldLog = session.hasEnded || (!!this.activeLesson && this.activeLesson.session.id !== session.id)
    const lesson_id = session.moduleId
    // Create the DayPlanAndModules object
    const planAndModules: DayPlanAndModules = {
      plan,
      modules: [module],
      dateOnly: new DateOnly(module.lastStudyDate),
      overall: new OverallStats(this.uid??''),
    }
    // Create the RangedModuleStats object and populate its properties
    const stats = new RangedModuleStats(module.moduleId, UserStatsRange.Daily, DateOnly.toTimestamp())
    Object.assign(stats, {
      highestCorrectStreak: session.highestCorrectStreak,
      rightAcquisitionCount: session.rightAcquisitionCount,
      wrongAcquisitionCount: session.wrongAcquisitionCount,
      rightReviewCount: session.rightReviewCount,
      wrongReviewCount: session.wrongReviewCount,
      rightPracticeCount: session.rightPracticeCount,
      wrongPracticeCount: session.wrongPracticeCount,
      studyTime: session.duration,
    })
    // Set the active lesson
    this.activeLesson = isQuizStudyPhase(phase) ? undefined : { planAndModules, stats, session, module, isFirstTime:false }
    // GA bailout
    if (!shouldLog) { return }
    const moduleStats = this.getModuleStats(module.moduleId) ?? new ModuleStats({lesson_id})
    // log try out
    if (!isRegularStudyPhase(phase)) { this.logLessonTryout({lesson_id}); return }
    // log lesson first time
    if (moduleStats.progress === 0) {
      if (this.activeLesson) { this.activeLesson.isFirstTime = true }
      this.logLessonStart({lesson_id})
    } else if (moduleStats.progress === 1) {
      this.logLessonCompleted({lesson_id})
    }
    // log daily plan start/completed
    if (plan.progress === 0) {
      this.logDailyPlanStart({lesson_id})
    } else if (plan.progress === 1) {
      this.logDailyPlanCompleted({lesson_id})
    }
    // log daily lesson start/completed
    if (module.progress === 0) {
      this.logDailyLessonStart({lesson_id})
    } else if(module.progress === 1) {
      this.logDailyLessonCompleted({lesson_id})
    }
    // log session start/completed
    if (session.duration === 0) {
      this.logSessionStart({lesson_id})
    } else if(session.hasEnded && module.isFinished) {
      this.logSessionCompleted({lesson_id})
    }
  }

  getModuleStats(moduleId: string) {
    return this._moduleStatsMap.get(moduleId)
  }

  private getNextModuleIdLogic(
    modules: { id: string; moduleId?: string; isFinished: boolean }[],
    currentModuleId: string,
    idProperty: 'moduleId' = 'moduleId'
  ): string | undefined {
    // First check if current module exists and is not finished
    const currentModule = modules.find(m => m[idProperty] === currentModuleId);
    if (currentModule && !currentModule.isFinished) {
      return currentModule[idProperty];
    }

    // If current module is finished or doesn't exist, find next unfinished module
    const unfinishedModules = modules.filter(m => !m.isFinished);
    if (unfinishedModules.length === 0) {
      return undefined;
    }

    // If current module exists (but is finished), find its position to start search
    const currentIndex = currentModule
      ? modules.findIndex(m => m[idProperty] === currentModuleId)
      : -1;

    if (currentIndex === -1) {
      // Current module not found - return first unfinished
      return unfinishedModules[0][idProperty];
    }

    // Find next unfinished module after current position
    for (let i = 1; i < modules.length; i++) {
      const nextIndex = (currentIndex + i) % modules.length;
      const module = modules[nextIndex];
      if (!module.isFinished) {
        return module[idProperty];
      }
    }

    return undefined; // Should never reach here due to earlier checks
  }

  // Async version
  getNextModuleId(currentModuleId: string): Observable<string | undefined> {
    return this.getDayPlanAndModules(new DateOnly()).pipe(
      map(dayPlanAndModules =>
        this.getNextModuleIdLogic(dayPlanAndModules.modules, currentModuleId)
      )
    );
  }

  // Sync version
  getNextModuleIdSync(currentModuleId: string): string | undefined {
    const modules = this._dayPlanAndModulesMap.get(new DateOnly().toISOString())?.modules ?? [];
    return this.getNextModuleIdLogic(modules, currentModuleId);
  }

  get isSingleSessionExit() {
    return this.activeLesson?.session?.hasEnded && this._moduleStatsMap.size === 1
  }
  get isFirstSessionExit() {
    return this.isSingleSessionExit && this.activeLesson?.isFirstTime
  }
  get isFirstLessonComplete() {
    return this.isSingleSessionExit && this.activeLesson?.module.isFinished
  }

  userProfileCollection = collection(this.firestore, 'users')

  private _defaultStudySetting = { duration: 2, difficulty: 2, soundEffect: true, animation: true }

  private _includedModuleInfo: IncludedModuleInfo[] = []
  get IncludedModuleInfo() { return this._includedModuleInfo }

  private readonly _dayPlanAndModulesMap = new Map<string, DayPlanAndModules>()
  private readonly _moduleStatsMap = new Map<string, ModuleStats>()
  private readonly _spacedRepConfigMap = new Map<string, SpacedRepModuleConfig>()

  private _previousUid: string | undefined

  clear() {
    this._includedModuleInfo = []
    this._dayPlanAndModulesMap?.clear()
    this._moduleStatsMap?.clear()
    this._spacedRepConfigMap?.clear()
  }

  override onUserChange(user: User | undefined) {
    return super.onUserChange(user).pipe(
      tap((newUser) => {
        const newUid = newUser?.uid
        if (newUid !== this._previousUid) {
          this.clear()
          this._previousUid = newUid
        }
      }),
      switchMap((user) => {
        if (user || this.userService.activeUser) {
          this.moduleStats.subscribe(stats => stats.forEach((stat) => this._moduleStatsMap.set(stat.id, stat)))
        }
        return ((user || this.userService.activeUser) ? this.includedModules : of([])).pipe(map(() => user))
      })
    )
  }

  setSetting(value: ApplicationSetting, change = {}, uid = this.uid) {
    return this.setAppSetting(value, uid).then(() => this.logSettingEvent(this.activeLesson?.session.moduleId, change))
  }

  getStudySetting(uid = this.uid) {
    return this.getAppSetting(uid)
    .pipe(
      map(setting => (setting?.studySetting ?? this._defaultStudySetting) as StudySetting),
      distinctUntilChanged(isEqual)
    )
  }

  // day plan and modules

  private _upsert(item: DayPlanAndModules, map = this._dayPlanAndModulesMap) {
    if (!item?.dateOnly) { return item }
    const previous = map.get(item.dateOnly.toISOString())
    const didChange = navigator.onLine && !(previous && this._isSameDayPlanAndModules(item, previous))
    if (didChange) { this._dayPlanAndModulesMap.set(item.dateOnly.toISOString(), item) }
    return didChange ? item : previous
  }

  private _isSameDayPlanAndModules(a: DayPlanAndModules, b: DayPlanAndModules) {
    return DateOnly.isEqual(a.dateOnly, b.dateOnly)
      && DayPlan.isEqual(a.plan, b.plan)
      && !DayPlanModule.isNewer(a.modules, b.modules)
      && OverallStats.isEqual(a.overall, b.overall)
  }

  // update the 5 days sliding window
  refreshDayPlanAndModules(hintDate: DateOnly = new DateOnly()) {
    if (!navigator.onLine) {
      // Return cached data for the 5-day window if offline
      const cachedData = Array.from(this._dayPlanAndModulesMap.values())
      return cachedData.length ? from(cachedData) : from([this.createDefaultDayPlanAndModules(hintDate)])
    }

    const today = new DateOnly();
    const [minDate, maxDate] = [today.addDays(-2), today.addDays(+2)];

    // Cleanup entries outside the sliding window
    this._dayPlanAndModulesMap.forEach(p => {
      if (!p.dateOnly.isBetween(minDate, maxDate)) {
        this._dayPlanAndModulesMap.delete(p.dateOnly.toISOString());
      }
    });

    // Generate days in the window and reorder to prioritize hintDate & today
    const windowDays = [-2, -1, 0, 1, 2].map(offset => today.addDays(offset));
    const isHintInWindow = hintDate.isBetween(minDate, maxDate);
    const isHintToday = hintDate.isSame(today);

    const reorderedDays = [...windowDays];

    if (isHintInWindow && !isHintToday) {
      // Move hintDate to the front if it's not today
      const hintIndex = reorderedDays.findIndex(d => d.isSame(hintDate));
      if (hintIndex !== -1) {
        reorderedDays.splice(hintIndex, 1);
        reorderedDays.unshift(hintDate);
      }
    }
    // Ensure today is second if hintDate is not today, or first if hintDate is today
    const todayIndex = reorderedDays.findIndex(d => d.isToday());
    if (todayIndex !== -1) {
      reorderedDays.splice(todayIndex, 1);
      if (isHintInWindow && !isHintToday) {
        reorderedDays.splice(1, 0, today);
      } else {
        reorderedDays.unshift(today);
      }
    }

    // Generate sources for reordered days
    const sources = reorderedDays.map(d => {
      const found = this._dayPlanAndModulesMap.get(d.toISOString());

      // Prioritize real-time updates for hintDate (if in window) and today
      if ((isHintInWindow && d.isSame(hintDate)) || d.isToday()) {
        return found
          ? merge(
              of(found),
              this.fetchDayPlanAndModules(d).pipe(
                tap(fetchedData => this._dayPlanAndModulesMap.set(d.toISOString(), fetchedData))
              )
            )
          : this.fetchDayPlanAndModules(d).pipe(
              tap(fetchedData => this._dayPlanAndModulesMap.set(d.toISOString(), fetchedData))
            );
      } else {
        return found
          ? of(found)
          : this.fetchDayPlanAndModules(d).pipe(
              tap(fetchedData => this._dayPlanAndModulesMap.set(d.toISOString(), fetchedData))
            );
      }
    });

    return merge(...sources);
  }

  private createDefaultDayPlanAndModules(date: DateOnly): DayPlanAndModules {
    return {
      dateOnly: date,
      plan: new DayPlan({ date: date.toTimestamp() }),
      modules: [],
      overall: new OverallStats(this.uid ?? '')
    };
  }

  getDayPlanAndModules(date = new DateOnly(), uid = this.uid) {
    const found = this._dayPlanAndModulesMap.get(date.toISOString())
    const result = found ? of(found) : of()
    return uid
    ? navigator.onLine
      ? merge(result, this.fetchDayPlanAndModules(date))
      : result
    : of()
  }

  fetchDayPlanAndModules(date = new DateOnly()) {
    return this._getDayplanId(date)
    .pipe(
      concatMap<string, Observable<DayPlanAndModules>>((planId: string) => {
        const dateOnly = [date]
        const modules = this.getDayPlanModules(planId)
        const plan = this.getDayPlan(planId, date)
        const overall = this.getOverallStats()
        return combineLatest({dateOnly, plan, modules, overall})
      }),
      distinctUntilChanged(this._isSameDayPlanAndModules),
      map(result => this._upsert(result) ?? result),
      catchError(() => of(this.createDefaultDayPlanAndModules(date))) // Fallback to default
    )
  }

  // update day plan modules on duration change

  updateModuleTargets(module: DayPlanModule, duration: number, rep: SpacedRepModuleConfig) {
    module.acquisitionTarget = Math.max(0, rep.dailyStudyCount[duration] - module.acquisitionProgress)
    module.isAcquisitionFinished = module.acquisitionProgress >= module.acquisitionTarget
    module.isFinished = module.progress >= 1 || (module.isReviewFinished && module.isAcquisitionFinished)
    module.estimatedCompletionTime = (
      Math.max(0, module.acquisitionTarget - module.acquisitionProgress) +
      Math.max(0, module.reviewTarget - module.reviewProgress)
    ) * Constants.ESTIMATED_TIME_PER_CARD
    return module
  }

  updateDayPlanModulesAndSetting(setting: ApplicationSetting, change: unknown = {}): Observable<DayPlanModuleSetting> {
    const didChange = this.userService.appSettingChanged(change, 'duration')
    return !didChange
    ? this.getDayPlanAndModules().pipe(
      take(1),
      map(({plan, modules}) => ({plan, modules, setting}))
    )
    : this.getDayPlanAndModules().pipe(
      take(1),
      switchMap(({modules, plan}) =>
        modules.length === 0 ? of({modules: [], plan, setting}) :
        from(modules).pipe(
          map(module => ({module, plan})),
          mergeMap(({module, plan}) => this.getSpacedRepConfig(module.moduleId).pipe(
            mergeMap(rep => this.updateModule(module, plan, setting, rep))
          )),
          take(2*modules.length),
          toArray(),
          map(updatedModules => ({modules: updatedModules, plan, setting}))
        )
      ),
      concatMap(this.finalizeUpdate.bind(this)),
    )
  }

  private finalizeUpdate(result: DayPlanModuleSetting): Observable<DayPlanModuleSetting>  {
    this.updateDayPlanAndModulesMap(result.modules??[])
    return of(result)
  }

  private async updateModule(
    module: DayPlanModule,
    plan: DayPlan,
    setting: ApplicationSetting,
    rep: SpacedRepModuleConfig
  ): Promise<DayPlanModule> {
    if (!plan.id || !rep.dailyStudyCount.includes(module.acquisitionTarget)) { return module }
    this.updateModuleTargets(module, setting.studySetting.duration, rep)
    await this.setDayPlanModule(module, plan.id)
    return module
  }

  private async updateDayPlanAndModulesMap(modules: DayPlanModule[]) {
    const dateKey = new DateOnly().toISOString()
    const found = this._dayPlanAndModulesMap.get(dateKey)
    if (found) {
      found.modules = modules
      this._upsert(found)
    }
  }

  setDayPlanModule(value: DayPlanModule, planId: string, uid = this.uid) {
    if (!uid) { return Promise.reject('no active user') }
    if (!value.id) { return Promise.reject(`${value ? 'invalid' : 'undefined'} plan module ${value ? JSON.stringify(value) : ''}`) }
    const ref = doc(this.firestore, `${DayPlanModule.collectionPath(planId)}/${value.id}`)
    return navigator.onLine ? updateDoc(ref, DayPlanModule.converter.toFirestore(value)).catch(error => { log.debug(value, error) }) : Promise.resolve()
  }

  // study order progress

  getStudyOrderProgress(lesson_id: string, uid = this.uid) {
    if (!uid) { log.debug(`no active user`); return of() }
    const refTemplate = doc(this.firestore, `${StudyOrderTemplate.collectionPath()}/${lesson_id}`)
    const ref = doc(this.firestore, `${StudyOrderProgress.collectionPath(uid)}/${lesson_id}`)
    return combineLatest([docData(ref), docData(refTemplate)]).pipe(
      map(([s,t]) => StudyOrderProgress.converter.fromFirestoreDocumentData({...s, moduleId: lesson_id}, {...t, moduleId: lesson_id}))
    )
  }

  setStudyOrderProgress(value: StudyOrderProgress, uid = this.uid) {
    if (!uid) { return Promise.reject('no active user') }
    if (!value.moduleId) { return Promise.reject(`${value ? 'invalid' : 'undefined'} study order progress ${value ? JSON.stringify(value) : ''}`) }
    const studyOrderProgress = new StudyOrderProgress(value.moduleId, value.remainingStudyOrders, value.finishedStudyOrders)
    const ref = doc(this.firestore, `${StudyOrderProgress.collectionPath(uid)}/${value.moduleId}`)
    return navigator.onLine ? updateDoc(ref, StudyOrderProgress.converter.toFirestore(studyOrderProgress)).catch(error => { log.debug(value, error) }) : Promise.resolve()
  }

  updateStudyOrderProgress(cards: FrontendCard[], studyOrderProgress: StudyOrderProgress) {
    const value = new StudyOrderProgress(studyOrderProgress.moduleId ?? '', studyOrderProgress.remainingStudyOrders, studyOrderProgress.finishedStudyOrders)
    cards.forEach(card => {
      if(card.stats.score >= 1) {
        value.moveToFinished(card.id)  // move from remaining to finished
      } else {
        value.moveToRemaining(card.id) // move from finished to remaining
      }
    })
    return isEqual(value, studyOrderProgress) ? Promise.resolve() : this.setStudyOrderProgress(value)
  }

  // stats

  completedPercentForDate(date: DateOnly = new DateOnly()) {
    const plan = this._dayPlanAndModulesMap.get(date.toISOString())?.plan
    return plan ? Math.max(plan.progress, plan.finishedModuleCount / ((plan.finishedModuleCount + plan.inprogressModuleCount + plan.todoModuleCount) || 1)) : 0
  }

  getOverallStats(uid = this.uid) {
    if (!uid) { return of(new OverallStats('')) }
    return navigator.onLine ? docData(doc(this.firestore, `${OverallStats.collectionPath()}/${uid}`)) as Observable<OverallStats> : of(new OverallStats(''))
  }

  // day plan

  private _getDayplanId(date = new DateOnly(), uid = this.uid) {
    if (!uid) { return of('') }
    //! backend day & month index start at 1
    const args = {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      createIfMissing: date.isSameOrAfter(new DateOnly())
    }
    return this.studyGetDayPlan(args)
    .pipe(
      catchError((val: FirebaseError) => { log.debug(val.stack, args); return of({documentId:''}) }),
      map(response => response?.documentId ?? ''),
    )
  }

  getDayPlan(plan_id: string, date: DateOnly) {
    if (!plan_id) { return of(new DayPlan({date:date.toTimestamp()})) }
    return navigator.onLine
    ? docData(doc(this.firestore, `${DayPlan.collectionPath()}/${plan_id}`))
      .pipe(
        catchError((val: FirebaseError) => { log.debug(val.stack); return of(new DayPlan()) }),
        map(plan => new DayPlan({ ...plan, id: plan_id })),
      )
    : of(new DayPlan({date:date.toTimestamp()}))
  }

  updateDayPlan(plan?: DayPlan) {
    if (!plan?.id) { return Promise.reject(`${plan ? 'invalid' : 'undefined'} plan ${plan ? JSON.stringify(plan) : ''}`) }
    const ref = doc(this.firestore, `${DayPlan.collectionPath()}/${plan.id}`)
    return navigator.onLine ? setDoc(ref, DayPlan.converter.toFirestore(plan)).catch(error => log.debug(plan, error)) : Promise.resolve()
  }

  // day plan modules

  getDayPlanModules(plan_id: string) {
    if (!plan_id) { return of([]) }
    if (!navigator.onLine) { return of([])}
    const refq = query(collection(this.firestore, `${DayPlanModule.collectionPath(plan_id)}`), orderBy('includedDate', 'desc'))
    return collectionSnapshots(refq)
    .pipe(
      catchError((val: FirebaseError) => { log.debug(val.stack); return [] }),
      map(items => items.map(item => DayPlanModule.converter.fromFirestore(item))),
      map(items => uniqWith(items.map(item => items.filter(it => it.moduleId === item.moduleId).sort(DayPlanModule.compare)[0]), isEqual)),
      map(items => this.backendService.applicationSkin === 'ASL' ? items.filter(i => i.moduleId.startsWith('ASL')) : items),
      map(items => DayPlanModule.sortAndPartitionModules(items))
    )
  }

  updateDayPlanModule(module: DayPlanModule, plan: DayPlan) {
    if (!module?.id) { return Promise.reject(`${module ? 'invalid' : 'undefined'} plan module ${module ? JSON.stringify(module) : ''}`) }
    if (!plan?.id) { return Promise.reject(`${plan ? 'invalid' : 'undefined'} parent plan ${plan ? JSON.stringify(plan) : ''}`) }
    const ref = doc(this.firestore, `${DayPlanModule.collectionPath(plan.id)}/${module.id}`)
    return navigator.onLine ? setDoc(ref, DayPlanModule.converter.toFirestore(module)).catch(error => log.debug(module, plan, error)) : Promise.resolve()
  }

  // cards

  requestCards(request: ReviewCardRetrievalRequest | AcquisitionCardRetrievalRequest | PracticeCardRetrievalRequest | QuizCardRetrievalRequest, uid = this.uid) {
    if (!uid) { return of([]) }
    if (!navigator.onLine) { return of([]) }
    return this.studyGetCards(request)
    .pipe(
      catchError((val: FirebaseError) => { log.debug(val.stack, request); return [] }),
      tap(response => log.debug('query cards', request, response)),
      map((cards => cards.map(card => new FrontendCard(card.template, isRegularStudyPhase(request.studyPhase) ? card.stats : undefined)))),
    )
  }

  // card stats

  getLastAcquiredCardId(lesson_id: string, uid = this.uid) {
    if (!uid) { return of('') }
    const refq = query(collection(this.firestore, `${CardStats.collectionPath(uid, lesson_id)}`),
      orderBy('reviewDueDate', 'desc'),
      limit(1)
    )
    return (refq ? collectionSnapshots(refq) : of())
    .pipe(
      catchError((val: FirebaseError) => { log.debug(val.stack); return [] }),
      mapToEntities<CardStats>(), map(s => (s.length > 0 ? s[0].cardId : undefined)))
  }

  getCardStats(lesson_id: string, cardId: string, uid = this.uid) {
    if (!uid) { return of(new CardStats(cardId)) }
    return navigator.onLine ? docData(doc(this.firestore, `${CardStats.collectionPath(uid, lesson_id)}/${cardId}`)) as Observable<CardStats> : of(new CardStats(cardId))
  }

  getCardStatsForModule(lesson_id: string, uid = this.uid) {
    if (!uid) { return of([]) }
    const refq = query(collection(this.firestore, `${CardStats.collectionPath(uid, lesson_id)}`))
    return (refq ? collectionSnapshots(refq) : of([]))
    .pipe(
      catchError((val: FirebaseError) => { log.debug(val.stack); return [] }),
      mapToEntities<CardStats>()
    )
  }

  setCardStats(stats: CardStats, moduleId: string, uid = this.uid) {
    if (!uid) { return Promise.reject('no valid user') }
    if (!stats.cardId) { return Promise.reject(`invalid stats ${JSON.stringify(stats)}`) }
    const ref = doc(this.firestore, `${CardStats.collectionPath(uid, moduleId)}/${stats.cardId}`)
    return navigator.onLine ? setDoc(ref, CardStats.converter.toFirestore(stats)).catch(error => { log.debug(stats, error) }) : Promise.resolve()
  }

  // user response

  createUserResponse(response: UserResponse, sessionId: string) {
    if (!(response && sessionId)) { return Promise.reject(`undefined ${response ? 'sessionId' : 'userResponse'} ${response ? (JSON.stringify(response)) : ''}`)}
    response.id = doc(collection(this.firestore, `${UserResponse.collectionPath(sessionId)}`)).id
    const ref = doc(this.firestore, `${UserResponse.collectionPath(sessionId)}`)
    return navigator.onLine ? setDoc(ref, UserResponse.converter.toFirestore(response)).catch(error => { log.debug(response, error) }) : Promise.resolve()
  }

  // session

  updateStudySession(session: StudySession) {
    if (!(session && session.id)) { return Promise.reject(`${session ? 'invalid' : 'undefined'} session ${session ? JSON.stringify(session) : ''}`) }
    const ref = doc(this.firestore, `${StudySession.collectionPath()}/${session.id}`)
    return navigator.onLine ? setDoc(ref, StudySession.converter.toFirestore(session)).then(() => session).catch(error => { log.debug(session, error); return undefined }) : Promise.resolve(session)
  }

  startStudySession(dayPlanDocId: string, moduleId: string, uid = this.uid) {
    if (!uid) { return Promise.reject('no valid user') }
    const session = new StudySession(dayPlanDocId, moduleId, uid)
    session.id = doc(collection(this.firestore, `${StudySession.collectionPath()}`)).id
    return this.updateStudySession(session)
  }

  endStudySession(phase: StudyPhase, plan: DayPlan, module: DayPlanModule, session: StudySession, studyOrderProgress?: StudyOrderProgress, uid = this.uid) {
    if (!plan?.id) { return Promise.reject(`${plan ? 'invalid' : 'undefined'} parent plan ${plan ? JSON.stringify(plan) : ''}`) }
    if (!module?.id && !isQuizStudyPhase(phase)) { return Promise.reject(`${module ? 'invalid' : 'undefined'} plan module ${module ? JSON.stringify(module) : ''}`) }
    if (!(session && session.id && !session.hasEnded)) { return Promise.reject(`${session ? 'invalid' : 'undefined'} session ${session ? JSON.stringify(session) : ''}`) }

    session.hasEnded = true

    const batch = writeBatch(this.firestore)
    let ref: DocumentReference
    let data: unknown

    ref = doc(this.firestore, `${StudySession.collectionPath()}/${session.id}`)
    data = StudySession.converter.toFirestore(session)
    if (navigator.onLine) { batch.set(ref, data) } else { setDoc(ref, data) }

    if (!isQuizStudyPhase(phase)) {
      ref = doc(this.firestore, `${DayPlanModule.collectionPath(plan.id)}/${module.id}`)
      data = DayPlanModule.converter.toFirestore(module)
      if (navigator.onLine) { batch.set(ref, data) } else { setDoc(ref, data) }

      if (uid && studyOrderProgress) {
        ref = doc(this.firestore, `${StudyOrderProgress.collectionPath(uid)}/${module.moduleId}`)
        data = StudyOrderProgress.converter.toFirestore(studyOrderProgress)
        if (navigator.onLine) { batch.set(ref, data) } else { setDoc(ref, data) }
      }
    }

    return (navigator.onLine ? batch.commit() : Promise.resolve())
    .then(() => log.debug('session batch committed', session, module, studyOrderProgress))
    .catch(error => log.error(`⚠️${error}`, plan, module, session, studyOrderProgress ?? '' ))
    .finally(() => this.setActiveLesson(phase, plan, module, session))
  }

  updateUserResponse(plan: DayPlan, module: DayPlanModule, session: StudySession, response: UserResponse|undefined, stats: CardStats|undefined, studyOrderProgress?: StudyOrderProgress, uid = this.uid) {
    if (!uid) { return Promise.reject('no valid user') }
    if (!session?.id) { return Promise.reject(`${session ? 'invalid' : 'undefined'} session ${session ? JSON.stringify(session) : ''}`) }
    if (!module?.moduleId) { return Promise.reject(`${module ? 'invalid' : 'undefined'} plan module ${module ? JSON.stringify(module) : ''}`) }
    if (!plan?.id) { return Promise.reject(`${plan ? 'invalid' : 'undefined'} plan ${plan ? JSON.stringify(plan) : ''}`) }

    const batch = writeBatch(this.firestore)
    let ref: DocumentReference
    let data: unknown

    if (response) {
      response.id = doc(collection(this.firestore, `${UserResponse.collectionPath(session.id)}`)).id
      ref = doc(this.firestore, `${UserResponse.collectionPath(session.id)}/${response.id}`)
      data = UserResponse.converter.toFirestore(response)
      if (navigator.onLine) { batch.set(ref, data) } else { setDoc(ref, data) }
    }

    ref = doc(this.firestore, `${StudySession.collectionPath()}/${session.id}`)
    data = StudySession.converter.toFirestore(session)
    if (navigator.onLine) { batch.set(ref, data) } else { setDoc(ref, data) }

    if (stats && isRegularStudyPhase(response?.studyPhase)) {
      if (!stats.cardId) { return Promise.reject(`invalid stats ${JSON.stringify(stats)}`) }
      ref = doc(this.firestore, `${CardStats.collectionPath(uid, module.moduleId)}/${stats.cardId}`)
      data = CardStats.converter.toFirestore(stats)
      if (navigator.onLine) { batch.set(ref, data) } else { setDoc(ref, data) }

      if (stats && studyOrderProgress) {
        if(stats.score >= 1) {
          studyOrderProgress.moveToFinished(stats.cardId)
        } else {
          studyOrderProgress.moveToRemaining(stats.cardId)
        }
        ref = doc(this.firestore, `${StudyOrderProgress.collectionPath(uid)}/${module.moduleId}`)
        data = StudyOrderProgress.converter.toFirestore(studyOrderProgress)
        if (navigator.onLine) { batch.set(ref, data) } else { setDoc(ref, data) }
        this._updateDayPlanModule(module)
      }
    }

    return (navigator.onLine ? batch.commit() : Promise.resolve())
    .then(() => log.debug('response batch committed', response, stats, module, session, studyOrderProgress))
    .catch((error) => log.error(`⚠️${error}`, response, stats, session, module, plan))
    .finally(() => this.setActiveLesson(response?.studyPhase ?? StudyPhase.Practice, plan, module, session))
  }

  // spaced repetition config

  getSpacedRepConfig(moduleId: string) {
    const ref = doc(this.firestore, `${SpacedRepModuleConfig.collectionPath()}/${moduleId}`)
    return docData(ref)
    .pipe(
      catchError((val: FirebaseError) => { log.debug(val.stack); return of()}),
      map(r => ({...r, id:ref.id} as SpacedRepModuleConfig))
    )
  }

  // application profile

  get profile() {
    if (!this.uid) { return of() }
    return docData(doc(this.firestore, `${ApplicationProfile.collectionPath()}`)) as Observable<ApplicationProfile>
  }

  // stats

  get todayStats() {
    if (!this.uid) { return of() }
    const date = new DateOnly().toISOString()
    return docData(doc(this.firestore, `${RangedOverallStats.collectionPath(this.uid, UserStatsRange.Daily)}/${date}`)) as Observable<RangedModuleStats>
  }

  get overallStats() {
    if (!this.uid) { return of() }
    return docData(doc(this.firestore, `${OverallStats.collectionPath()}/${this.uid}`)) as Observable<OverallStats>
  }

  get moduleStats() {
    if (!this.uid) { return of([]) }
    const ref = collection(this.firestore, `${ModuleStats.collectionPath(this.uid)}`)
    return (ref ? collectionSnapshots(ref) : of([]))
    .pipe(
      takeUntil(of(this.uid)),
      catchError((val: FirebaseError) => { log.debug(val.stack); return [] }),
      map(cs => cs.map(c => ModuleStats.converter.fromFirestore(c)))
    )
  }

  getStatsForModule(id: string, uid = this.uid) {
    if (!uid) { return of() }
    const ref = doc(this.firestore, `${ModuleStats.collectionPath(uid)}/${id}`)
    return docData(ref)
    .pipe(
      catchError((val: FirebaseError) => { log.debug(val.stack); return [] }),
      map(c => c ? new ModuleStats(c) : new ModuleStats({id}))
    )
  }

  getDailyStatsForModule(id: string, date = new DateOnly(), uid = this.uid) {
    if (!uid) { return of() }
    const range = UserStatsRange.Daily
    const docId = `${date.toISOString()}-${id}`
    const refq = query(collection(this.firestore, `${RangedModuleStats.collectionPath(uid, range)}`), where(documentId(), '==', docId))
    return collectionSnapshots(refq)
    .pipe(
      catchError((val: FirebaseError) => { log.debug(val.stack); return [] }),
      map(cs => cs[0] ? RangedModuleStats.converter.fromFirestore(cs[0]) : new RangedModuleStats(id, range, date.toTimestamp())),
    )
  }

  // modules

  get includedModules() {
    if (!this.uid || !this.firestore) { return of([]) }
    const ref = collection(this.firestore, `${IncludedModuleInfo.collectionPath(this.uid)}`)
    return (ref ? collectionSnapshots(ref) : of([]))
    .pipe(
      map(ms => ms.map(m => new IncludedModuleInfo(m.id, m.data()['includedDate'].toDate()))),
      map(m => this._updateIncludedModules(m))
    )
  }

  private _updateIncludedModules(items: IncludedModuleInfo[]) {
    const didChange = navigator.onLine && !isEqual(items, this._includedModuleInfo)
    if (didChange) { this._includedModuleInfo = items }
    return this._includedModuleInfo
  }

  private _updateDayPlanModule(module: DayPlanModule) {
    const found = this._includedModuleInfo.findIndex(m => m.moduleId === module.moduleId)
    if (found >= 0) { this._includedModuleInfo[found].isFinished = module.isFinished }
  }

  private async _postIncludeUpdateDayPlan(update = true, timeoutDuration = 6000): Promise<DayPlanAndModules[] | undefined> {
    return firstValueFrom(
      update
      ? this.fetchDayPlanAndModules().pipe(take(1), toArray(), timeout(timeoutDuration))
      : of(undefined)
    )
  }

  async includeModule(lesson_id: string | undefined, uid = this.uid) {
    if (!uid) return Promise.reject('no valid user')
    if (!lesson_id) return Promise.reject('no module id provided')
    if (!navigator.onLine) return Promise.resolve(true)

    try {
      const exists = await firstValueFrom(this.existModule(lesson_id))
      if (exists) {
        log.debug(`already subscribed to ${lesson_id}`)
        return true
      }

      const ref = doc(this.firestore, `${IncludedModuleInfo.collectionPath(uid)}/${lesson_id}`)
      const included = new IncludedModuleInfo(lesson_id, DateOnly.toTimestamp())

      await Promise.all([
        setDoc(ref, IncludedModuleInfo.converter.toFirestore(included)).catch(error => {log.debug(ref, error)}),
        this._postIncludeUpdateDayPlan()
      ])

      this.logLessonAdded({ lesson_id })
      return true
    } catch (error) {
      log.debug(error)
      return false
    }
  }

  async includeModules(
    lesson_ids: string[],
    uid = this.uid,
    statusCallback?: (lesson_id: string, status: boolean) => void
  ): Promise<boolean[]> {
    if (!uid) return Promise.reject('no valid user')
    if (!lesson_ids.length) return Promise.reject('no module ids provided')
    if (!navigator.onLine) return Promise.resolve(Array(lesson_ids.length).fill(true))

    return Promise.all(lesson_ids.map(async (lesson_id) => {
      const exists = await firstValueFrom(this.existModule(lesson_id)).catch(error => {
        log.debug(`Error checking existence for ${lesson_id}:`, error)
        statusCallback?.(lesson_id, false)
        return false
      })

      if (exists) {
        log.debug(`already subscribed to ${lesson_id}`)
        statusCallback?.(lesson_id, true)
        return true
      }

      const ref = doc(this.firestore, `${IncludedModuleInfo.collectionPath(uid)}/${lesson_id}`)
      const included = new IncludedModuleInfo(lesson_id, DateOnly.toTimestamp())

      const added = await Promise.all([
        setDoc(ref, IncludedModuleInfo.converter.toFirestore(included)),
        this._postIncludeUpdateDayPlan(false)
      ]).then(() => {
        this.logLessonAdded({ lesson_id })
        statusCallback?.(lesson_id, true)
        return true
      }).catch(error => {
        log.debug(`Error adding module ${lesson_id}:`, error)
        statusCallback?.(lesson_id, false)
        return false
      })

      return added
    }))
  }

  removeModule(lesson_id: string|undefined, uid = this.uid) {
    if (!uid) { return Promise.reject('no valid user') }
    if (!lesson_id) { return Promise.reject('no module id') }
    const ref = doc(this.firestore, `${IncludedModuleInfo.collectionPath(uid)}/${lesson_id}`)
    return deleteDoc(ref)
    .then(() => lastValueFrom(this.fetchDayPlanAndModules().pipe(take(1))).then(v => { log.debug('post lesson remove plan', v); return true}))
    .catch((error) => {log.debug(error); return false})
    .finally(() => this.logLessonRemoved({lesson_id}))
  }

  existModule(lesson_id: string, uid = this.uid) {
    if (!uid) { return of(false) }
    const ref = doc(this.firestore, `${IncludedModuleInfo.collectionPath(uid)}/${lesson_id}`)
    return from(this.refExists(ref))
  }

  getPlanModule(lesson_id: string|undefined, date = new DateOnly) {
    return !!lesson_id && this.isIncludedModule(lesson_id) ? this._dayPlanAndModulesMap.get(date.toISOString())?.modules.find(m => m.moduleId === lesson_id) : undefined
  }

  isIncludedModule(lesson_id: string|undefined, date = new DateOnly) {
    return !!lesson_id && !(this._includedModuleInfo.findIndex(m => m.moduleId === lesson_id && new DateOnly(m.includedDate).isSameOrBefore(date)) < 0)
  }

  isPlanModule(lesson_id: string|undefined, date = new DateOnly) {
    return !!lesson_id && this.isIncludedModule(lesson_id) && !!this.getPlanModule(lesson_id, date)
  }

  isFinishedModule(lesson_id?: string, date = new DateOnly()) {
    const module = lesson_id && this.isIncludedModule(lesson_id) ? this.getPlanModule(lesson_id, date) : null;
    return module?.isFinished ?? false
  }
}
